import { getPersonKey } from '../pages/DataMerginAndValidation/match-display-rows'
import { msg } from '@lingui/macro'

const MERGE_CASES = {  MERGE_ROAD: 'merge_road',
  MERGE_UNIT: 'merge_unit',
  MERGE_PERSON: 'merge_person',
  MERGE_CARRIED_PERSON: 'merge_carried_person',
}

const UPDATE_CASES = {
  SAVE_CRASH: 'save_crash',
  SAVE_UNIT: 'save_unit',
  SAVE_PERSON: 'save_person',
  SAVE_CARRIED_PERSON: 'save_carried_person',
}

/**
 * @typedef {'crash_road' | 'unit' | 'person' | 'carried'} ValidationStepKind
 */

/**
 * @typedef {Object} ValidationStep
 * @property {ValidationStepKind} kind
 * @property {number} [unitIndex]
 * @property {number} [personMergeIndex]
 * @property {Record<string, unknown>} [person]
 * @property {string} [role]
 */

/**
 * @param {{
 *   unitSlots?: Array<{ done?: boolean }>,
 *   peopleToMerge?: Array<{ done?: boolean }>,
 *   carriedPeople?: Array<{ done?: boolean, carryRole?: string }>,
 *   includeCrashRoad?: boolean,
 * }} params
 * @returns {ValidationStep[]}
 */
export function buildValidationSteps({
  unitSlots = [],
  peopleToMerge = [],
  carriedPeople = [],
  includeCrashRoad = true,
}) {
  /** @type {ValidationStep[]} */
  const steps = []

  if (includeCrashRoad) {
    steps.push({ kind: 'crash_road' })
  }

  unitSlots.forEach((_, unitIndex) => {
    steps.push({ kind: 'unit', unitIndex })
  })

  peopleToMerge.forEach((_, personMergeIndex) => {
    steps.push({ kind: 'person', personMergeIndex })
  })

  carriedPeople.forEach((person) => {
    if (!person?.carryRole) return
    steps.push({
      kind: 'carried',
      person,
      role: person.carryRole,
    })
  })

  return steps
}

/**
 * @param {ValidationStep[]} steps
 * @param {{
 *   crashDone?: boolean,
 *   unitSlots?: Array<{ done?: boolean }>,
 *   peopleToMerge?: Array<{ done?: boolean }>,
 *   carriedPeople?: Array<{ done?: boolean, carryRole?: string } & Record<string, unknown>>,
 * }} progress
 */
export function findFirstUndoneStepIndex(steps, progress) {
  const {
    crashDone = false,
    unitSlots = [],
    peopleToMerge = [],
    carriedPeople = [],
  } = progress

  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index]
    if (step.kind === 'crash_road' && !crashDone) return index
    if (step.kind === 'unit' && !unitSlots[step.unitIndex]?.done) return index
    if (step.kind === 'person' && !peopleToMerge[step.personMergeIndex]?.done) {
      return index
    }
    if (step.kind === 'carried') {
      const carried = carriedPeople.find(
        (person) =>
          person.carryRole === step.role &&
          getPersonKey(person) === getPersonKey(step.person),
      )
      if (!carried?.done) return index
    }
  }

  return steps.length > 0 ? 0 : -1
}

/**
 * @param {ValidationStep[]} steps
 * @param {Record<string, unknown>} mergeState
 * @param {Array<Record<string, unknown>>} peopleToMerge
 */
export function findStepIndexForMergeState(steps, mergeState, peopleToMerge = []) {
  if (!mergeState?.type || steps.length === 0) return -1

  return steps.findIndex((step) => {
    if (step.kind === 'crash_road') {
      return mergeState.type === UPDATE_CASES.SAVE_CRASH
    }
    if (step.kind === 'unit') {
      return (
        mergeState.type === UPDATE_CASES.SAVE_UNIT &&
        mergeState.index === step.unitIndex
      )
    }
    if (step.kind === 'person') {
      const person = peopleToMerge[step.personMergeIndex]
      return (
        mergeState.type === UPDATE_CASES.SAVE_PERSON &&
        (person ? getPersonKey(person) === mergeState.personKey : false)
      )
    }
    if (step.kind === 'carried') {
      return (
        mergeState.type === UPDATE_CASES.SAVE_CARRIED_PERSON &&
        mergeState.role === step.role &&
        mergeState.personKey === getPersonKey(step.person)
      )
    }
    return false
  })
}

export { MERGE_CASES as VALIDATION_MERGE_CASES, UPDATE_CASES as VALIDATION_UPDATE_CASES }

/**
 * @param {ValidationStep | undefined} step
 * @param {number} stepIndex
 * @param {number} totalSteps
 * @param {(message: import('@lingui/macro').MessageDescriptor) => string} translate
 */
export function getValidationStepLabel(step, stepIndex, totalSteps, translate) {
  if (!step) {
    return translate(msg`Validation step`)
  }

  const prefix =
    totalSteps > 1
      ? `${stepIndex + 1}/${totalSteps} — `
      : ''

  switch (step.kind) {
    case 'crash_road':
      return `${prefix}${translate(msg`Crash & Road`)}`
    case 'unit':
      return `${prefix}${translate(msg`Unit`)} ${step.unitIndex + 1}`
    case 'person':
      return `${prefix}${translate(msg`Person`)} ${step.personMergeIndex + 1}`
    case 'carried':
      return `${prefix}${translate(msg`Included person`)}`
    default:
      return `${prefix}${translate(msg`Section`)}`
  }
}

/**
 * @param {ValidationStep} step
 * @param {import('react').Dispatch<{ type: string, index?: number, person?: Record<string, unknown>, role?: string }>} dispatchMergeState
 */
export function dispatchValidationStep(step, dispatchMergeState) {
  switch (step.kind) {
    case 'crash_road':
      dispatchMergeState({ type: MERGE_CASES.MERGE_ROAD })
      break
    case 'unit':
      dispatchMergeState({ type: MERGE_CASES.MERGE_UNIT, index: step.unitIndex })
      break
    case 'person':
      dispatchMergeState({
        type: MERGE_CASES.MERGE_PERSON,
        index: step.personMergeIndex,
      })
      break
    case 'carried':
      dispatchMergeState({
        type: MERGE_CASES.MERGE_CARRIED_PERSON,
        person: step.person,
        role: step.role,
      })
      break
    default:
      break
  }
}

/**
 * @param {ValidationStep[]} steps
 * @param {ValidationStep} targetStep
 */
export function findStepIndexForTargetStep(steps, targetStep) {
  return steps.findIndex((step) => {
    if (step.kind !== targetStep.kind) return false
    if (step.kind === 'crash_road') return true
    if (step.kind === 'unit') return step.unitIndex === targetStep.unitIndex
    if (step.kind === 'person') {
      return step.personMergeIndex === targetStep.personMergeIndex
    }
    if (step.kind === 'carried') {
      return (
        step.role === targetStep.role &&
        getPersonKey(step.person) === getPersonKey(targetStep.person)
      )
    }
    return false
  })
}
